import { useState, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { REGISTRATION_ENABLED } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Trophy, Users, Calendar, Crown, AlertCircle, KeyRound } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import BracketTreeView from '../BracketTreeView';
import CompactMatchCard from './CompactMatchCard';
import GroupStandings from './GroupStandings';
import CollapsibleBrackets from './CollapsibleBrackets';
import EliminationBracketTree from './EliminationBracketTree';
import EliminationSeedingTable from './EliminationSeedingTable';
import { generateTournamentData } from '@/utils/tournamentDataGenerator';
import { parseLocalDate } from '@/utils/dateUtils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserGroupInfo } from '@/hooks/useUserGroupInfo';
import { useGroupStandings } from '@/hooks/useGroupStandings';
import { useEventRules } from '@/hooks/useEventRules';
import { useSubcategoryParticipants } from '@/hooks/useSubcategoryParticipants';
import ParticipantsList from './ParticipantsList';
import { useUserRegistration } from '@/hooks/useUserRegistration';
import { useTournamentSchedule } from '@/hooks/useTournamentSchedule';
import { useEliminationSeeding } from '@/hooks/useEliminationSeeding';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { Clock, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNextUpCourt } from '@/hooks/useNextUpCourt';
interface Tournament {
  id: string | number;
  title: string;
  sport: string;
  startDate: string;
  endDate: string;
  location: string;
  tournamentFormat: string;
  matchType: 'singles' | 'doubles';
  partner?: string;
  status: 'upcoming' | 'active' | 'finished';
  nextMatch?: {
    opponent: string;
    court: string;
    time: string;
  };
  organizer?: string;
  time?: string;
  category?: string;
  gameType?: 'singles' | 'doubles';
  currentStage?: string;
  participants?: number;
  maxParticipants?: number;
  rules?: any;
}

interface TournamentRoundRobinViewProps {
  tournament: Tournament;
  onRegisterClick?: () => void;
  onAccessCodeClick?: () => void;
  isUserRegistered?: boolean;
  registrationLoading?: boolean;
}

const TournamentRoundRobinView = ({ tournament, onRegisterClick, onAccessCodeClick, isUserRegistered, registrationLoading }: TournamentRoundRobinViewProps) => {
  const [showBrackets, setShowBrackets] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>('Grupo A');
  const [tournamentRules, setTournamentRules] = useState<any>(null);
  const [eliminationConfig, setEliminationConfig] = useState<any>(null);
  const isMobile = useIsMobile();
  const { language, t } = useLanguage();
  const { nextUpCourt, nextUpCategory, nextUpSubcategory, nextUpPhase } = useNextUpCourt(String(tournament.id));

  // Helper: case/accent-insensitive equality
  const sameStr = (a?: string | null, b?: string | null) => {
    const norm = (s?: string | null) => (s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return norm(a) === norm(b);
  };
  // Helper: does the active notification target a given (category, subcategory, phaseGroup)?
  // phaseGroup: 'grupos' | 'eliminacion'. Backward-compat: if metadata is null, treat as match.
  const notifMatches = (cat?: string | null, subcat?: string | null, phaseGroup?: 'grupos' | 'eliminacion') => {
    if (!nextUpCourt) return false;
    if (nextUpCategory && cat && !sameStr(nextUpCategory, cat)) return false;
    if (nextUpSubcategory && subcat && !sameStr(nextUpSubcategory, subcat)) return false;
    if (nextUpPhase && phaseGroup) {
      const np = (nextUpPhase || '').toLowerCase();
      const isElim = np === 'eliminacion' || np === 'octavos' || np === 'cuartos' || np === 'semifinales' || np === 'final' || np.includes('elimin');
      const isGroup = np === 'grupos' || np.startsWith('grupo');
      if (phaseGroup === 'eliminacion' && !isElim) return false;
      if (phaseGroup === 'grupos' && !isGroup) return false;
    }
    return true;
  };
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  const translateGroupName = useCallback((name: string): string => {
    if (language === 'es') return name;
    return name.replace(/^Grupo\b/i, t('tournament.groupPrefix'));
  }, [language, t]);

  const translateCourt = useCallback((court: string): string => {
    if (language === 'es') return court;
    return court.replace(/^Cancha\b/i, t('tournament.court'));
  }, [language, t]);

  const translateSpanishDate = useCallback((text: string): string => {
    if (language === 'es') return text.charAt(0).toUpperCase() + text.slice(1);
    const days: Record<string, string> = { lunes: 'Monday', martes: 'Tuesday', miércoles: 'Wednesday', miercoles: 'Wednesday', jueves: 'Thursday', viernes: 'Friday', sábado: 'Saturday', sabado: 'Saturday', domingo: 'Sunday' };
    const months: Record<string, string> = { enero: 'January', febrero: 'February', marzo: 'March', abril: 'April', mayo: 'May', junio: 'June', julio: 'July', agosto: 'August', septiembre: 'September', octubre: 'October', noviembre: 'November', diciembre: 'December' };
    let result = text.toLowerCase().replace(/\bde\b/g, '').replace(/,/g, '').replace(/\s+/g, ' ').trim();
    for (const [es, en] of Object.entries(days)) { result = result.replace(new RegExp(`\\b${es}\\b`, 'i'), en); }
    for (const [es, en] of Object.entries(months)) { result = result.replace(new RegExp(`\\b${es}\\b`, 'i'), en); }
    // Reorder to "Friday, April 17" format
    const match = result.match(/^(\w+)\s+(\d+)\s+(\w+)$/);
    if (match) result = `${match[1]}, ${match[3]} ${match[2]}`;
    return result;
  }, [language]);

  const formatMatchDate = useCallback((dateStr: string) => {
    if (!dateStr) return '';
    const isoMatch = dateStr.match(/^\d{4}-\d{2}-\d{2}/);
    if (!isoMatch) {
      return translateSpanishDate(dateStr);
    }
    const d = parseLocalDate(dateStr);
    if (isNaN(d.getTime())) return translateSpanishDate(dateStr);
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    const formatted = d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }, [language, translateSpanishDate]);

  const onCarouselSelect = useCallback(() => {
    if (!carouselApi) return;
    setCurrentSlide(carouselApi.selectedScrollSnap());
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;
    onCarouselSelect();
    carouselApi.on('select', onCarouselSelect);
    return () => { carouselApi.off('select', onCarouselSelect); };
  }, [carouselApi, onCarouselSelect]);
  
  // Parse tournament rules
  useEffect(() => {
    if (tournament.rules) {
      try {
        const parsed = typeof tournament.rules === 'string' 
          ? JSON.parse(tournament.rules) 
          : tournament.rules;
        setTournamentRules(parsed);
        console.log('[TournamentRoundRobinView] Tournament rules parsed:', {
          hasGenderCategories: !!parsed?.genderCategories,
          genderCategoriesKeys: parsed?.genderCategories ? Object.keys(parsed.genderCategories) : [],
          masculinoRules: parsed?.genderCategories?.Masculino,
          fullParsed: parsed
        });
      } catch (err) {
        console.error('[TournamentRoundRobinView] Error parsing tournament rules:', err);
      }
    } else {
      console.log('[TournamentRoundRobinView] No tournament.rules available:', tournament.rules);
    }
  }, [tournament.rules]);

  // Fetch elimination_config from group_rules
  useEffect(() => {
    const fetchEliminationConfig = async () => {
      const { data } = await supabase
        .from('events')
        .select('group_rules')
        .eq('id', tournament.id.toString())
        .single();
      if (data?.group_rules) {
        try {
          const gr = typeof data.group_rules === 'string' ? JSON.parse(data.group_rules) : data.group_rules;
          if (gr?.elimination_config) {
            const config = { ...gr.elimination_config };
            if (gr?.adjustments) config.adjustments = gr.adjustments;
            setEliminationConfig(config);
          }
        } catch (e) {
          console.error('Error parsing group_rules for elimination_config:', e);
        }
      }
    };
    if (tournament.id) fetchEliminationConfig();
  }, [tournament.id]);

  // Debug logs for group info
  console.log('[TournamentRoundRobinView] Fetching group info:', {
    tournamentId: tournament.id,
    tournamentIdType: typeof tournament.id,
    tournamentIdString: tournament.id.toString(),
    tournamentTitle: tournament.title,
    tournamentStatus: tournament.status
  });
  
  // Fetch real group info from database (supports multiple registrations)
  const { groupInfo, allGroupInfos, loading: groupLoading } = useUserGroupInfo(tournament.id.toString());
  
  // State for selecting active group when user has multiple registrations
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const activeGroupInfo = allGroupInfos.length > 0 ? allGroupInfos[activeGroupIndex] || allGroupInfos[0] : groupInfo;

  // Reset carousel slide when active category/group changes
  useEffect(() => {
    setCurrentSlide(0);
    if (carouselApi) {
      try { carouselApi.scrollTo(0); } catch {}
    }
  }, [activeGroupIndex, carouselApi]);
  
  // Get user's registration to get category/subcategory directly
  const { registration } = useUserRegistration(tournament.id.toString());
  
  console.log('[TournamentRoundRobinView] Group info result:', {
    hasGroupInfo: !!groupInfo,
    groupLoading,
    groupName: groupInfo?.groupName,
    groupCategory: groupInfo?.category,
    participantsCount: groupInfo?.participants?.length,
    matchesCount: groupInfo?.matches?.length
  });

  // Fetch real standings from database - prioritize registration data over groupInfo
  const standingsCategory = activeGroupInfo?.category ?? registration?.category ?? groupInfo?.category ?? '';
  const standingsSubcategory = activeGroupInfo?.subcategory ?? registration?.subcategory ?? groupInfo?.subcategory ?? undefined;
  
  const { 
    standings: realGroupStandings, 
    loading: standingsLoading 
  } = useGroupStandings(
    tournament.id.toString(),
    activeGroupInfo?.groupName ?? groupInfo?.groupName ?? '',
    standingsCategory,
    standingsSubcategory
  );

  console.log('[TournamentRoundRobinView] Standings params:', {
    groupName: groupInfo?.groupName,
    standingsCategory,
    standingsSubcategory,
    registrationCategory: registration?.category,
    registrationSubcategory: registration?.subcategory,
    groupInfoCategory: groupInfo?.category,
    groupInfoSubcategory: groupInfo?.subcategory
  });

  // Fetch event rules to know how many teams qualify
  const { rules } = useEventRules(tournament.id.toString());
  
  // Get category-specific rules from tournamentRules.genderCategories - use useMemo for proper reactivity
  const { qualifyingPositions, wildCardCount } = useMemo(() => {
    console.log('[TournamentRoundRobinView] getCategoryRules recalculating:', {
      hasTournamentRules: !!tournamentRules,
      hasGenderCategories: !!tournamentRules?.genderCategories,
      hasRegistration: !!registration,
      registrationCategory: registration?.category,
      registrationSubcategory: registration?.subcategory
    });
    
    if (!tournamentRules?.genderCategories || !registration) {
      return { qualifyingPositions: 1, wildCardCount: 0 };
    }
    
    // First try with the direct category from registration
    let categoryList = tournamentRules.genderCategories[registration.category];
    
    // If not found, try with normalized key (Masculino/Femenino)
    if (!categoryList || !Array.isArray(categoryList)) {
      const normalizedKey = registration.category?.toLowerCase().includes('masculin') 
        ? 'Masculino' 
        : registration.category?.toLowerCase().includes('femenin')
        ? 'Femenino'
        : registration.category;
      
      console.log('[TournamentRoundRobinView] Trying normalized key:', normalizedKey);
      categoryList = tournamentRules.genderCategories[normalizedKey];
    }
    
    if (!categoryList || !Array.isArray(categoryList)) {
      console.log('[TournamentRoundRobinView] No category list found for:', registration.category);
      return { qualifyingPositions: 1, wildCardCount: 0 };
    }
    
    // Find matching subcategory - try registration.subcategory first, then groupInfo as fallback
    const subcategoryToMatch = registration.subcategory || groupInfo?.subcategory;
    const categoryRules = categoryList.find((cat: any) => 
      cat.category === subcategoryToMatch ||
      cat.category === registration.subcategory ||
      cat.fullName === groupInfo?.category  // e.g., "Masculino 1era"
    );
    
    if (categoryRules) {
      console.log('[TournamentRoundRobinView] Category rules found:', {
        qualifiersPerGroup: categoryRules.qualifiersPerGroup,
        wildCardCount: categoryRules.wildCardCount
      });
      return {
        qualifyingPositions: categoryRules.qualifiersPerGroup || 1,
        wildCardCount: categoryRules.wildCardCount || 0
      };
    }
    
    // If no exact match, try first item in category list as fallback
    if (categoryList.length > 0) {
      console.log('[TournamentRoundRobinView] Using first category as fallback:', categoryList[0]);
      return {
        qualifyingPositions: categoryList[0].qualifiersPerGroup || 1,
        wildCardCount: categoryList[0].wildCardCount || 0
      };
    }
    
    console.log('[TournamentRoundRobinView] No matching subcategory found:', subcategoryToMatch);
    return { qualifyingPositions: 1, wildCardCount: 0 };
  }, [tournamentRules, registration, groupInfo]);
  
  // Fetch participants in the same subcategory
  const { 
    participants: subcategoryParticipants,
    loading: participantsLoading,
    currentUserId,
    totalCount: participantsCount
  } = useSubcategoryParticipants(
    tournament.id.toString(),
    activeGroupInfo?.subcategory ?? registration?.subcategory ?? null,
    activeGroupInfo?.category ?? registration?.category ?? null
  );

  console.log('[TournamentRoundRobinView] Participantes Debug:', {
    eventId: tournament.id.toString(),
    subcategory: groupInfo?.subcategory,
    category: groupInfo?.category,
    subcategoryType: typeof groupInfo?.subcategory,
    subcategoryValue: JSON.stringify(groupInfo?.subcategory),
    participantsCount,
    participantsLoading
  });

  // Fetch elimination bracket data from tournament_schedules filtered by category
  const { 
    eliminationMatches, 
    loading: eliminationLoading 
  } = useTournamentSchedule(
    tournament.id.toString(),
    activeGroupInfo?.category ?? registration?.category ?? null,
    activeGroupInfo?.subcategory ?? registration?.subcategory ?? null
  );

  // Fetch elimination seeding data
  const { 
    seeding: eliminationSeeding, 
    loading: seedingLoading 
  } = useEliminationSeeding(
    tournament.id.toString(),
    activeGroupInfo?.category ?? registration?.category ?? null,
    activeGroupInfo?.subcategory ?? registration?.subcategory ?? null
  );
  
  const { matches, standings } = useMemo(() => {
    // Transform tournament data to match expected interface
    const tournamentEvent = {
      ...tournament,
      organizer: tournament.organizer || 'Organizador',
      time: tournament.time || '10:00',
      category: tournament.category || 'A',
      tournamentFormat: tournament.tournamentFormat as 'round-robin' | 'sumatoria-juegos' | 'eliminacion-directa'
    };
    return generateTournamentData(tournamentEvent);
  }, [tournament]);
  
  const isFinished = tournament.status === 'finished';

  // Generate different mock data based on tournament status (only for active/finished)
  const groupMatches = matches || (() => {
    const userTeam = tournament.partner ? `Tú/${tournament.partner}` : 'Tú';
    
    if (tournament.status === 'active') {
      return [
        {
          id: 1,
          teams: userTeam,
          opponent: 'López/Martín',
          date: '15 Jul',
          time: '16:00',
          court: '2',
          result: 'Ganaste 2-1',
          status: 'on-time' as 'on-time' | 'delayed' | 'cancelled',
          isUpcoming: false,
          sets: [
            { userGames: 6, opponentGames: 4 },
            { userGames: 3, opponentGames: 6 },
            { userGames: 6, opponentGames: 2 }
          ],
          totalGamesFor: 15,
          totalGamesAgainst: 12
        },
        {
          id: 2,
          teams: userTeam,
          opponent: 'García/Rodríguez',
          date: '16 Jul',
          time: '10:30',
          court: '1',
          result: null,
          status: 'on-time' as const,
          isUpcoming: true,
          sets: [],
          totalGamesFor: 0,
          totalGamesAgainst: 0
        },
        {
          id: 3,
          teams: userTeam,
          opponent: 'Silva/Torres',
          date: '17 Jul',
          time: '14:30',
          court: '3',
          result: null,
          status: 'on-time' as const,
          isUpcoming: true,
          sets: [],
          totalGamesFor: 0,
          totalGamesAgainst: 0
        }
      ];
    }
    
    // finished status
    return [
      {
        id: 1,
        teams: userTeam,
        opponent: 'López/Martín',
        date: '15 Jul',
        time: '16:00',
        court: '2',
        result: 'Ganaste 2-1',
        status: 'on-time' as 'on-time' | 'delayed' | 'cancelled',
        isUpcoming: false,
        sets: [
          { userGames: 6, opponentGames: 4 },
          { userGames: 3, opponentGames: 6 },
          { userGames: 6, opponentGames: 2 }
        ],
        totalGamesFor: 15,
        totalGamesAgainst: 12
      },
      {
        id: 2,
        teams: userTeam,
        opponent: 'García/Rodríguez',
        date: '16 Jul',
        time: '10:30',
        court: '1',
        result: 'Perdiste 1-2',
        status: 'on-time' as const,
        isUpcoming: false,
        sets: [
          { userGames: 6, opponentGames: 3 },
          { userGames: 4, opponentGames: 6 },
          { userGames: 2, opponentGames: 6 }
        ],
        totalGamesFor: 12,
        totalGamesAgainst: 15
      },
      {
        id: 3,
        teams: userTeam,
        opponent: 'Silva/Torres',
        date: '16 Jul',
        time: '14:30',
        court: '3',
        result: 'Ganaste 2-0',
        status: 'on-time' as const,
        isUpcoming: false,
        sets: [
          { userGames: 6, opponentGames: 4 },
          { userGames: 6, opponentGames: 3 }
        ],
        totalGamesFor: 12,
        totalGamesAgainst: 7
      }
    ];
  })();

  const groupData = useMemo(() => {
    // Si tenemos datos reales del grupo, usarlos
    if (realGroupStandings && realGroupStandings.length > 0) {
      console.log('[TournamentRoundRobinView] Usando clasificaciones reales:', realGroupStandings);
      return realGroupStandings;
    }

    // Fallback a datos mockeados solo si no hay datos reales
    console.log('[TournamentRoundRobinView] No hay datos reales, usando mock');
    return standings || (() => {
      const userTeam = tournament.partner ? `Tú/${tournament.partner}` : 'Tú';
      
      if (tournament.status === 'active') {
        return [
          { position: 1, team: 'García/Rodríguez', wins: 1, losses: 0, gamesFor: 12, gamesAgainst: 8, points: 2, isUser: false },
          { position: 2, team: userTeam, wins: 1, losses: 0, gamesFor: 15, gamesAgainst: 12, points: 2, isUser: true },
          { position: 3, team: 'López/Martín', wins: 0, losses: 1, gamesFor: 12, gamesAgainst: 15, points: 0, isUser: false },
          { position: 4, team: 'Silva/Torres', wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0, points: 0, isUser: false }
        ];
      }
      
      // finished status
      return [
        { position: 1, team: userTeam, wins: 2, losses: 1, gamesFor: 39, gamesAgainst: 34, points: 4, isUser: true },
        { position: 2, team: 'García/Rodríguez', wins: 2, losses: 1, gamesFor: 33, gamesAgainst: 28, points: 4, isUser: false },
        { position: 3, team: 'López/Martín', wins: 1, losses: 2, gamesFor: 24, gamesAgainst: 30, points: 2, isUser: false },
        { position: 4, team: 'Silva/Torres', wins: 1, losses: 2, gamesFor: 20, gamesAgainst: 24, points: 2, isUser: false }
      ];
    })();
  }, [realGroupStandings, standings, tournament]);

  // Helper function to format date in Spanish
  const formatDate = (dateStr: string) => {
    if (!dateStr) return language === 'en' ? 'To be decided' : 'Por definir';
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const locale = language === 'en' ? 'en-US' : 'es-ES';
      const weekday = date.toLocaleDateString(locale, { weekday: 'long' });
      const monthName = date.toLocaleDateString(locale, { month: 'long' });
      const dayNum = date.getDate();
      if (language === 'en') {
        return `${weekday}, ${monthName} ${dayNum}`;
      }
      return `${weekday}, ${dayNum} ${monthName}`;
    } catch {
      return dateStr;
    }
  };

  // Transform elimination matches to bracket format
  const scheduledBrackets = useMemo(() => {
    if (!eliminationMatches || eliminationMatches.length === 0) {
      return { octavos: [], cuartos: [], semifinales: [], final: [] };
    }
    
    // Mapear las fases desde los códigos del schedule a los nombres del bracket
    const phaseMap: Record<string, 'octavos' | 'cuartos' | 'semifinales' | 'final'> = {
      'Oct': 'octavos',
      'R16': 'octavos',
      'QF': 'cuartos',
      'SF': 'semifinales',
      'Final': 'final'
    };
    
    // Agrupar por fase
    const grouped = {
      octavos: [] as any[],
      cuartos: [] as any[],
      semifinales: [] as any[],
      final: [] as any[]
    };
    
    eliminationMatches.forEach((match: any, index: number) => {
      const phaseName = phaseMap[match.phase];
      if (phaseName) {
        grouped[phaseName].push({
          id: index + 1,
          player1: match.participants && match.participants[0] ? match.participants[0] : 'TBD',
          player2: match.participants && match.participants[1] ? match.participants[1] : 'TBD',
          date: formatDate(match.date),
          time: match.startTime || '',
          court: match.court,
          result: undefined,
          sets: undefined
        });
      }
    });
    
    return grouped;
  }, [eliminationMatches]);

  // Generate mock data for all groups (for "Todos los Grupos" tab)
  const allGroupsData = [
    { 
      groupName: 'Grupo A',
      standings: groupData
    },
    {
      groupName: 'Grupo B',
      standings: [
        { position: 1, team: 'Fernández/Silva', wins: 3, losses: 0, gamesFor: 36, gamesAgainst: 18, points: 6, isUser: false },
        { position: 2, team: 'Morales/Castro', wins: 2, losses: 1, gamesFor: 30, gamesAgainst: 24, points: 4, isUser: false },
        { position: 3, team: 'Ruiz/Vega', wins: 1, losses: 2, gamesFor: 22, gamesAgainst: 30, points: 2, isUser: false },
        { position: 4, team: 'Méndez/Ortiz', wins: 0, losses: 3, gamesFor: 16, gamesAgainst: 32, points: 0, isUser: false }
      ]
    },
    {
      groupName: 'Grupo C',
      standings: [
        { position: 1, team: 'Torres/Jiménez', wins: 2, losses: 1, gamesFor: 32, gamesAgainst: 28, points: 4, isUser: false },
        { position: 2, team: 'Herrera/Peña', wins: 2, losses: 1, gamesFor: 31, gamesAgainst: 29, points: 4, isUser: false },
        { position: 3, team: 'Delgado/Vargas', wins: 1, losses: 2, gamesFor: 26, gamesAgainst: 30, points: 2, isUser: false },
        { position: 4, team: 'Ramírez/Soto', wins: 1, losses: 2, gamesFor: 25, gamesAgainst: 27, points: 2, isUser: false }
      ]
    }
  ];

  if (showBrackets) {
    return (
      <BracketTreeView 
        onBack={() => setShowBrackets(false)}
        bracketData={scheduledBrackets}
        sport={tournament.sport}
        gameType={tournament.gameType || tournament.matchType}
      />
    );
  }

  // Determine available tabs based on tournament status
  const getAvailableTabs = () => {
    const baseTabs = ['mi-grupo', 'participantes', 'eliminacion'];
    return baseTabs;
  };

  const availableTabs = getAvailableTabs();

  return (
    <div className="space-y-4">
      {/* Tournament Header with Progress Indicator */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-primary via-primary/80 to-primary/60 p-4">
          <h2 className="text-white font-bold text-lg mb-3">{tournament.title}</h2>
          
          {/* Phase Progress Indicator */}
          <div className="flex items-center justify-between relative px-4">
            {/* Progress bar background */}
            <div className="flex-1 h-1 bg-white/30 rounded-full absolute top-4 left-8 right-8" />
            
            {/* Progress bar fill - based on tournament status */}
            <div className={`h-1 bg-white rounded-full absolute top-4 left-8 transition-all duration-500 ${
              tournament.status === 'finished' 
                ? 'right-8' 
                : tournament.status === 'active' 
                  ? 'w-1/3' 
                  : 'w-0'
            }`} />
            
            <div className="flex items-center justify-between w-full relative z-10">
              {/* Fase de Grupos */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md transition-all ${
                  tournament.status === 'active' || tournament.status === 'finished'
                    ? 'bg-white text-primary ring-2 ring-white'
                    : 'bg-white/50 text-white'
                }`}>
                  <Users className="w-4 h-4" />
                </div>
                <span className="text-white text-xs mt-1 font-medium">{t('tournament.groups')}</span>
              </div>
              
              {/* Fase de Eliminación */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md transition-all ${
                  tournament.status === 'finished'
                    ? 'bg-white text-orange-600 ring-2 ring-white'
                    : 'bg-white/50 text-white'
                }`}>
                  <Trophy className="w-4 h-4" />
                </div>
                <span className="text-white text-xs mt-1 font-medium">{t('tournament.elimination')}</span>
              </div>
              
              {/* Final */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md transition-all ${
                  tournament.status === 'finished'
                    ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white ring-2 ring-yellow-300'
                    : 'bg-white/50 text-white'
                }`}>
                  <Crown className="w-4 h-4" />
                </div>
                <span className="text-white text-xs mt-1 font-medium">{t('tournament.final')}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Champion Banner for finished tournaments */}
      {isFinished && groupData[0]?.isUser && (
        <Card className="bg-gradient-to-r from-yellow-50 via-orange-50 to-yellow-50 border-yellow-300 shadow-lg animate-fade-in overflow-hidden">
          <CardContent className="p-4 relative">
            <div className="absolute top-0 right-0 text-6xl opacity-10">🎉</div>
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-yellow-900 text-lg">
                  {t('tournament.champion')} 🏆
                </h4>
                <p className="text-yellow-700 text-sm font-medium">
                  {tournament.partner ? `${t('tournament.you')}/${tournament.partner}` : t('tournament.you')} - {t('tournament.congratulations')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registration Buttons - Solo si NO está inscrito */}
      {!isUserRegistered && onRegisterClick && (
        <div className="px-0 space-y-2">
          {/* Access Code Button */}
          {onAccessCodeClick && (
            <Button
              variant="ghost"
              onClick={onAccessCodeClick}
              className="w-full text-slate-600 hover:text-slate-800 hover:bg-slate-100"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              {t('tournament.accessCodeQuestion')}
            </Button>
          )}
          {/* Main Registration Button */}
          <Button
            onClick={REGISTRATION_ENABLED ? onRegisterClick : undefined}
            disabled={registrationLoading || !REGISTRATION_ENABLED}
            className={`w-full h-16 text-lg font-bold shadow-lg hover:shadow-xl transition-all ${
              !REGISTRATION_ENABLED
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {registrationLoading ? t('tournament.loading') : t('tournament.signMeUp')}
          </Button>
        </div>
      )}

      {/* Main Tabs Interface */}
      <Tabs defaultValue={availableTabs[0]} className="w-full">
        <TabsList className={`grid w-full grid-cols-3 ${isMobile ? 'h-14' : 'h-12'} bg-gradient-to-r from-gray-100 to-gray-50 p-1 gap-1`}>
          <TabsTrigger 
            value="mi-grupo" 
            className={`${isMobile ? 'text-xs' : 'text-sm'} flex flex-col items-center gap-1 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all`}
          >
            <Users className="w-4 h-4" />
            <span className="font-medium">{t('tournament.myGroup')}</span>
          </TabsTrigger>
          <TabsTrigger 
            value="participantes" 
            className={`${isMobile ? 'text-xs' : 'text-sm'} flex flex-col items-center gap-1 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all relative`}
          >
            <Users className="w-4 h-4" />
            <span className="font-medium">{t('tournament.participants')}</span>
          </TabsTrigger>
          <TabsTrigger 
            value="eliminacion" 
            className={`${isMobile ? 'text-xs' : 'text-sm'} flex flex-col items-center gap-1 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all`}
          >
            <Trophy className="w-4 h-4" />
            <span className="font-medium">{t('tournament.elimination')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab Content */}
        <TabsContent value="mi-grupo" className="space-y-4 mt-4">
          <div className="space-y-4">
            {/* Category selector when user has multiple registrations */}
            {allGroupInfos.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {allGroupInfos.map((info, idx) => (
                  <Badge
                    key={idx}
                    variant={activeGroupIndex === idx ? 'default' : 'outline'}
                    className={`cursor-pointer text-sm px-3 py-1.5 transition-all ${
                      activeGroupIndex === idx 
                        ? 'bg-primary text-primary-foreground shadow-md' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => setActiveGroupIndex(idx)}
                  >
                    {info.category}{info.subcategory ? ` ${info.subcategory}` : ''}
                  </Badge>
                ))}
              </div>
            )}

            {/* Show match carousel if available */}
            {groupLoading ? (
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                </CardContent>
              </Card>
            ) : activeGroupInfo && activeGroupInfo.matches.length > 0 ? (
              (() => {
                const userParticipant = activeGroupInfo.participants.find(p => p.isUser);
                const userMatchesUnsorted = userParticipant 
                  ? activeGroupInfo.matches.filter(m => 
                      m.participants.includes(userParticipant.letter)
                    )
                  : activeGroupInfo.matches;
                
                // Sort by time ascending to guarantee chronological order
                const userMatches = [...userMatchesUnsorted].sort((a, b) => {
                  const ta = (a.time || '').padStart(5, '0');
                  const tb = (b.time || '').padStart(5, '0');
                  return ta.localeCompare(tb);
                });
                
                console.log('[TournamentRoundRobinView] 🎠 Carousel render:', {
                  activeGroupIndex,
                  activeCategory: activeGroupInfo.category,
                  activeSubcategory: activeGroupInfo.subcategory,
                  groupName: activeGroupInfo.groupName,
                  userLetter: userParticipant?.letter,
                  userName: userParticipant ? `${userParticipant.player1}${userParticipant.player2 ? '/' + userParticipant.player2 : ''}` : 'NONE',
                  userMatchesCount: userMatches.length,
                  userMatches: userMatches.map((m, i) => ({ idx: i, id: m.id, time: m.time, participants: m.participants }))
                });

                const getTeamName = (letter: string) => {
                  const p = activeGroupInfo.participants.find(pt => pt.letter === letter);
                  if (!p) return t('tournament.toBeAssigned');
                  return p.player2 ? `${p.player1} / ${p.player2}` : p.player1;
                };

                if (userMatches.length === 0) {
                  return (
                    <Card className="border-muted">
                      <CardContent className="p-6 text-center text-muted-foreground text-sm">
                        {t('tournament.noMatchesFound')}
                      </CardContent>
                    </Card>
                  );
                }

                return (
                  <>
                    <div className="flex items-center justify-end mb-2">
                      <Badge variant="secondary" className="text-sm font-semibold">
                        {currentSlide + 1}/{userMatches.length}
                      </Badge>
                    </div>
                    <Carousel setApi={setCarouselApi} className="w-full">
                      <CarouselContent>
                        {(() => {
                          // Calculate the index of the first pending (unfinished) match
                          const firstPendingIdx = userMatches.findIndex((m, i) => {
                            const [lA, lB] = m.participants;
                            const tA = getTeamName(lA);
                            const tB = getTeamName(lB);
                            const normalize = (n: string) => n?.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
                            const sm = eliminationMatches.find(em => {
                              if (!em.participants || em.participants.length < 2) return false;
                              const ep1 = normalize(em.participants[0]);
                              const ep2 = normalize(em.participants[1]);
                              return (ep1 === normalize(tA) && ep2 === normalize(tB)) || (ep1 === normalize(tB) && ep2 === normalize(tA));
                            });
                            const finished = !!(sm?.score || sm?.winnerTeam);
                            return !finished;
                          });
                          // Only show banner if notification targets this group's category/subcategory + grupos phase
                          const notifAppliesHere = notifMatches(
                            activeGroupInfo?.category ?? registration?.category,
                            activeGroupInfo?.subcategory ?? registration?.subcategory,
                            'grupos'
                          );
                          const effectiveNextUp = notifAppliesHere ? nextUpCourt : null;
                          return userMatches.map((match, idx) => {
                          const [letterA, letterB] = match.participants;
                          const teamA = getTeamName(letterA);
                          const teamB = getTeamName(letterB);
                          const isUserTeamA = userParticipant?.letter === letterA;

                          return (
                            <CarouselItem key={match.id || idx}>
                              {(() => {
                                const normalize = (n: string) => n?.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
                                const scheduleMatch = eliminationMatches.find(em => {
                                  if (!em.participants || em.participants.length < 2) return false;
                                  const ep1 = normalize(em.participants[0]);
                                  const ep2 = normalize(em.participants[1]);
                                  const ta = normalize(teamA);
                                  const tb = normalize(teamB);
                                  return (ep1 === ta && ep2 === tb) || (ep1 === tb && ep2 === ta);
                                });
                                const isFinished = !!(scheduleMatch?.score || scheduleMatch?.winnerTeam);
                                const court = scheduleMatch?.assignedCourt;

                                // Determine if user is team1 in the schedule (not by group letter)
                                const userTeamName = userParticipant
                                  ? (userParticipant.player2 ? `${userParticipant.player1} / ${userParticipant.player2}` : userParticipant.player1)
                                  : '';
                                const normUser = normalize(userTeamName);
                                const schedP1 = normalize(scheduleMatch?.participants?.[0] || '');
                                const isUserTeam1InSchedule = schedP1 === normUser;

                                // Determine result
                                let resultBadge: React.ReactNode = null;
                                if (isFinished && scheduleMatch?.winnerTeam != null) {
                                  const userWon = (scheduleMatch.winnerTeam === 1 && isUserTeam1InSchedule) || (scheduleMatch.winnerTeam === 2 && !isUserTeam1InSchedule);
                                  const isDraw = scheduleMatch.winnerTeam === 0;
                                  if (isDraw) {
                                    resultBadge = <Badge className="bg-muted text-muted-foreground text-xs">— {language === 'es' ? 'Empate' : 'Draw'}</Badge>;
                                  } else if (userWon) {
                                    resultBadge = <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">✓ {language === 'es' ? 'Ganaste' : 'Won'}</Badge>;
                                  } else {
                                    resultBadge = <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">✗ {language === 'es' ? 'Perdiste' : 'Lost'}</Badge>;
                                  }
                                }

                                const userWon = isFinished && scheduleMatch?.winnerTeam != null && scheduleMatch.winnerTeam !== 0 &&
                                  ((scheduleMatch.winnerTeam === 1 && isUserTeam1InSchedule) || (scheduleMatch.winnerTeam === 2 && !isUserTeam1InSchedule));
                                const userLost = isFinished && scheduleMatch?.winnerTeam != null && scheduleMatch.winnerTeam !== 0 && !userWon;

                                return (
                              <div className="border rounded-lg overflow-hidden bg-primary/5 border-primary/20">
                                <div className={`py-2 px-3 flex items-center justify-center gap-2 text-sm font-bold border-b ${
                                  isFinished
                                    ? 'bg-gray-100 text-gray-600 border-gray-200'
                                    : court
                                      ? 'bg-green-50 text-green-700 border-green-100'
                                      : (effectiveNextUp && idx === firstPendingIdx)
                                        ? 'bg-amber-50 text-amber-700 border-amber-100'
                                        : 'bg-gray-50 text-gray-400 border-gray-100'
                                }`}>
                                  <MapPin className="h-3.5 w-3.5" />
                                  <span>
                                     {isFinished
                                       ? (language === 'es' ? 'Finalizado' : 'Finished')
                                       : court
                                         ? `${language === 'es' ? 'Asignado en' : 'Assigned to'} ${court}`
                                         : (effectiveNextUp && idx === firstPendingIdx)
                                           ? `${language === 'es' ? 'Próximo en' : 'Next up on'} ${effectiveNextUp}`
                                           : (language === 'es' ? 'Sin asignar' : 'Unassigned')}
                                  </span>
                                </div>
                                <div className="p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                                      {translateGroupName(activeGroupInfo.groupName)}
                                    </Badge>
                                    {resultBadge}
                                  </div>
                                  <div className="space-y-2">
                                    <div className="text-center">
                                      <span className={`font-medium text-sm ${
                                        isFinished && userWon && isUserTeamA ? 'text-green-700 font-bold' :
                                        isFinished && userLost && isUserTeamA ? 'text-red-500 opacity-70' :
                                        isUserTeamA ? 'text-primary font-bold' : 'text-foreground'
                                      }`}>{teamA}</span>
                                    </div>
                                    <div className="text-center text-xs font-medium">
                                      {isFinished && scheduleMatch?.score
                                        ? <span className="text-green-700 font-bold">{scheduleMatch.score}</span>
                                        : <span className="text-muted-foreground">VS</span>
                                      }
                                    </div>
                                    <div className="text-center">
                                      <span className={`font-medium text-sm ${
                                        isFinished && userWon && !isUserTeamA ? 'text-green-700 font-bold' :
                                        isFinished && userLost && !isUserTeamA ? 'text-red-500 opacity-70' :
                                        !isUserTeamA ? 'text-primary font-bold' : 'text-foreground'
                                      }`}>{teamB}</span>
                                    </div>
                                    <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground pt-2 border-t border-primary/10">
                                      {match.date && (
                                        <div className="flex items-center space-x-1">
                                          <Calendar className="h-3.5 w-3.5" />
                                          <span>{formatMatchDate(match.date)}</span>
                                        </div>
                                      )}
                                      {match.time && (
                                        <div className="flex items-center space-x-1">
                                          <Clock className="h-3.5 w-3.5" />
                                          <span>{match.time}</span>
                                        </div>
                                      )}
                                      {(match.court || activeGroupInfo.court || effectiveNextUp) && (
                                        <div className="flex items-center space-x-1">
                                          <MapPin className="h-3.5 w-3.5" />
                                          <span>{(() => {
                                            const courtVal = (effectiveNextUp && !isFinished && idx === firstPendingIdx ? effectiveNextUp : '') || court || match.court || activeGroupInfo.court || '';
                                            const courtStr = String(courtVal);
                                            if (courtStr.toLowerCase().startsWith('cancha')) return translateCourt(courtStr);
                                            return `${t('tournament.court')} ${courtStr}`;
                                          })()}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                                );
                              })()}
                            </CarouselItem>
                          );
                        });
                        })()}
                      </CarouselContent>
                    </Carousel>
                    {userMatches.length > 1 && (
                      <>
                        <div className="flex items-center justify-center gap-4 mt-4">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => carouselApi?.scrollPrev()} disabled={currentSlide === 0}>
                            <ChevronLeft className="h-5 w-5" />
                          </Button>
                          <div className="flex gap-2">
                            {userMatches.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => carouselApi?.scrollTo(idx)}
                                className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentSlide ? 'bg-primary' : 'bg-muted hover:bg-muted-foreground/30'}`}
                                aria-label={`${t('tournament.goToMatch')} ${idx + 1}`}
                              />
                            ))}
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => carouselApi?.scrollNext()} disabled={currentSlide === userMatches.length - 1}>
                            <ChevronRight className="h-5 w-5" />
                          </Button>
                        </div>
                        <div className="text-center text-xs text-muted-foreground pt-2">
                          {userMatches.length} {t('tournament.scheduledMatches')}
                        </div>
                      </>
                    )}
                  </>
                );
              })()
            ) : activeGroupInfo ? (
              <Card className="border-muted">
                <CardContent className="p-6 text-center text-muted-foreground text-sm">
                  {t('tournament.matchesComingSoon')}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center gap-3">
                    <AlertCircle className="h-12 w-12 text-orange-600 dark:text-orange-400" />
                    <h3 className="text-lg font-bold text-orange-900 dark:text-orange-100">
                      {t('tournament.groupNotAssigned')}
                    </h3>
                    <p className="text-sm text-orange-700 dark:text-orange-300">
                      {t('tournament.groupAssignSoon')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}


            {/* My Group Standings - Only show when user has an assigned group */}
            {activeGroupInfo && groupData.length > 0 && (
              <GroupStandings 
                standings={groupData} 
                title={t('tournament.myGroupStandings')}
                groupName={activeGroupInfo.groupName}
              />
            )}

            {/* Elimination Seeding - show below group standings */}
            {activeGroupInfo && (
              <EliminationSeedingTable 
                seeding={eliminationSeeding}
                loading={seedingLoading}
                subcategory={activeGroupInfo?.subcategory ?? registration?.subcategory ?? ''}
                category={activeGroupInfo?.category ?? registration?.category ?? ''}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="participantes" className="space-y-4 mt-4">
          {allGroupInfos.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {allGroupInfos.map((info, idx) => (
                <Badge 
                  key={idx} 
                  variant={activeGroupIndex === idx ? 'default' : 'outline'}
                  className={`cursor-pointer text-sm px-3 py-1.5 transition-all ${
                    activeGroupIndex === idx 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => setActiveGroupIndex(idx)}
                >
                  {info.category}{info.subcategory ? ` ${info.subcategory}` : ''}
                </Badge>
              ))}
            </div>
          )}
          <ParticipantsList
            participants={subcategoryParticipants}
            currentUserId={currentUserId}
            loading={participantsLoading}
            subcategory={activeGroupInfo?.subcategory ?? registration?.subcategory ?? 'N/A'}
            category={activeGroupInfo?.category ?? registration?.category ?? 'N/A'}
          />
        </TabsContent>

        <TabsContent value="eliminacion" className="mt-4">
          {allGroupInfos.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {allGroupInfos.map((info, idx) => (
                <Badge 
                  key={idx} 
                  variant={activeGroupIndex === idx ? 'default' : 'outline'}
                  className={`cursor-pointer text-sm px-3 py-1.5 transition-all ${
                    activeGroupIndex === idx 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => setActiveGroupIndex(idx)}
                >
                  {info.category}{info.subcategory ? ` ${info.subcategory}` : ''}
                </Badge>
              ))}
            </div>
          )}
          <EliminationBracketTree
            eliminationMatches={eliminationMatches?.filter((m: any) => !m.phase?.toLowerCase().startsWith('grupo')) || []}
            player1Name={registration?.player1_name}
            player2Name={registration?.player2_name}
            nextUpCourt={notifMatches(
              activeGroupInfo?.category ?? registration?.category,
              activeGroupInfo?.subcategory ?? registration?.subcategory,
              'eliminacion'
            ) ? nextUpCourt : null}
            nextUpPhase={notifMatches(
              activeGroupInfo?.category ?? registration?.category,
              activeGroupInfo?.subcategory ?? registration?.subcategory,
              'eliminacion'
            ) ? nextUpPhase : null}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TournamentRoundRobinView;